(ns app.front.view.admin.tenant-management.external-module-subs
  "Events and subscriptions for a tenant's external products (PWAM): the
  catalogue that comes with the tenant data, and the commands of the product's
  row in the Products tab. Every write re-pulls the tenant, whose groups record
  the outcome."
  (:require [app.front.view.admin.tenant-management.subs :as tm-subs]
            [app.front.view.admin.tenant-management.user-subs :as user-subs]
            [core.front.operation-registry :as operation-registry]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

;; The enabled systems and their module (`[{:system :module-key :module-id
;; :module-name :roles :default-departments}]`); empty with the flag off.
(rf/reg-sub ::catalogue :-> (comp vec ::tm-subs/external-modules))

(rf/reg-sub ::catalogue-by-system
  :<- [::catalogue]
  (fn [catalogue _]
    (p/index-by :system catalogue)))

(rf/reg-event-fx ::refresh
  ;; Enabling creates groups and departments become user-groups, so both the
  ;; tenant tree and the Users tab's group list are re-pulled.
  (fn [_ [_ tenant-id]]
    {:fx [[:dispatch [::tm-subs/fetch-tenant tenant-id]]
          [:dispatch [::user-subs/fetch-users tenant-id]]]}))

(defn- save-in-flight?
  "Whether a save command of the product's row has not yet been answered."
  [db row-key]
  (->> (operation-registry/active-operations db)
       keys
       (some #(and (vector? %) (= [::save row-key] (pop %))))))

(rf/reg-sub ::draft
  ;; The unsaved edits of a product's row; `row-key` is `[tenant-id system]`.
  (fn [db [_ row-key]]
    (get-in db [::drafts row-key] {})))

(rf/reg-sub ::saving?
  ;; From Save until the tenant has been fetched again: enabling waits for the
  ;; other system to create the city, which can take a while.
  (fn [db [_ row-key]]
    (boolean (or (save-in-flight? db row-key)
                 (pos? (get-in db [::refreshing row-key] 0))))))

(rf/reg-event-db ::edit
  (fn [db [_ row-key changes]]
    (update-in db [::drafts row-key] merge changes)))

(rf/reg-event-db ::cancel
  (fn [db [_ row-key]]
    (update db ::drafts dissoc row-key)))

(rf/reg-event-fx ::save
  ;; `module` is `{:enabled? :renewal-date}` when the licence changed and
  ;; `departments` the full list when they did; the commands run side by side.
  (fn [_ [_ {:keys [tenant-id system module departments]}]]
    (let [row-key [tenant-id system]
          save    (fn [part command data]
                    [:dispatch [:comm/command-monitored
                                {:command          command
                                 :operation-id     [::save row-key part]
                                 :data             (assoc data :domain-group-id tenant-id :system system)
                                 :notify           {:success :changes-saved}
                                 :finally-dispatch [::save-done row-key]}]])]
      {:fx [(when module
              (save :module :admin/tenant-set-external-module
                    (p/assoc-if {:enabled? (:enabled? module)}
                                :renewal-date (:renewal-date module))))
            (when departments
              (save :departments :admin/tenant-set-external-module-departments
                    {:departments (vec departments)}))]})))

(rf/reg-event-fx ::save-done
  ;; A save is shown until the refetch it started is merged, so the row does
  ;; not show the old state in between.
  (fn [{:keys [db]} [_ [tenant-id :as row-key]]]
    {:db (update-in db [::refreshing row-key] (fnil inc 0))
     :fx [[:dispatch [::tm-subs/fetch-tenant-then tenant-id [::saved row-key]]]
          [:dispatch [::user-subs/fetch-users tenant-id]]]}))

(rf/reg-event-db ::saved
  ;; The draft goes once nothing of the save is still running, whatever the
  ;; outcome; a failure has shown its error.
  (fn [db [_ row-key]]
    (let [db (update-in db [::refreshing row-key] dec)]
      (if (or (save-in-flight? db row-key)
              (pos? (get-in db [::refreshing row-key])))
        db
        (-> db
            (update ::refreshing dissoc row-key)
            (update ::drafts dissoc row-key))))))

(rf/reg-event-fx ::retry-sync
  (fn [_ [_ {:keys [tenant-id system]}]]
    {:comm/command {:command          :admin/tenant-external-module-retry-sync
                    :data             {:domain-group-id tenant-id
                                       :system          system}
                    :notify           {:success :changes-saved}
                    :finally-dispatch [::refresh tenant-id]}}))
